import { useState, useEffect, useMemo } from 'react';
import { capitalize, initialScreenValues } from './_utils';
import { type ValidationObject } from './_types';

const defaultViewport = {
  mobile: 0,
  tablet: 834,
  desktop: 1024,
};

type DefaultType = keyof typeof defaultViewport;

function useWindowSize<T extends string = never>(customViewports?: Record<T, number>) {
  const viewportsObj = customViewports ?? defaultViewport;
  const breakpoints = useMemo(
    () =>
      (Object.keys(viewportsObj) as (keyof typeof viewportsObj)[]).sort(
        (vp1, vp2) => viewportsObj[vp1] - viewportsObj[vp2],
      ),
    [viewportsObj],
  );
  const [windowSize, setWindowSize] = useState(() => {
    const initialValidationValues = breakpoints.reduce((acc, curr) => {
      acc[`is${capitalize(curr)}`] = false;
      return acc;
    }, {} as ValidationObject<T, DefaultType>);

    return {
      ...initialScreenValues,
      ...initialValidationValues,
    };
  });

  useEffect(() => {
    function handleResize() {
      const { innerWidth, innerHeight } = window;
      const { offsetWidth } = document.body;
      const validationValues = breakpoints.reduce((acc, curr, index, arr) => {
        const currViewportSize = viewportsObj[curr as keyof typeof viewportsObj];

        const isSmallerThanNext =
          index === arr.length - 1 || innerWidth < viewportsObj[arr[index + 1] as keyof typeof viewportsObj];

        acc[`is${capitalize(curr)}`] = innerWidth >= currViewportSize && isSmallerThanNext;
        return acc;
      }, {} as ValidationObject<T, DefaultType>);
      setWindowSize({
        width: innerWidth,
        offsetWidth,
        height: innerHeight,
        screenHeight: window.screen.height,
        ...validationValues,
      });
    }

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return windowSize;
}

export default useWindowSize;
